import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { CiRunRow } from '../lib/ci';
import type { ToastState } from '../lib/toast';
import { CardGridSkeleton } from './CardGridSkeleton';
import { CiRunItem } from './CiRunItem';
import { EmptyState } from './EmptyState';
import { PageHeader } from './PageHeader';
import { Panel } from './Panel';
import { QueryBoundary } from './QueryBoundary';
import { SessionBadge } from './SessionBadge';
import { Toast } from './Toast';

// CI runs (arc 101 slice 07) — PRESENTER. Renders the page from already-fetched
// data + handlers; no data fetching here (that lives in the `routes/Ci` container).
// Container ≠ presenter, two reasons to change (dashboard file rules). Props in,
// JSX out — the `runs` query is passed straight through to the QueryBoundary.

export type CiViewProps = {
  readonly login: string | null;
  readonly meLoading: boolean;
  readonly hasSession: boolean;
  /** The `useCiRuns` query — its `{ isLoading, isError, data, refetch }` drives the boundary. */
  readonly runs: ReturnType<typeof import('../lib/ci').useCiRuns>;
  /** The rows to render — the `runs` read's answer, which the `ci` topic keeps
   * fresh by RE-READING it (`createLiveQuery`), never by painting stream rows. */
  readonly rows: () => readonly CiRunRow[];
  readonly onViewLog: (id: string) => void;
  /** The container's `createToast()` state — a failed presign speaks here (#924). */
  readonly toast: () => ToastState | null;
  readonly onDismissToast: () => void;
};

export function CiView(props: CiViewProps): JSX.Element {
  return (
    <section class="page ci">
      <PageHeader
        title={t('ci.title')}
        desc={t('ci.subtitle')}
        actions={<SessionBadge login={props.login} loading={props.meLoading} />}
      />

      <Show when={props.hasSession} fallback={<EmptyState message={t('ci.no_session')} />}>
        <Panel title={t('ci.runs_heading')}>
          <QueryBoundary
            query={props.runs}
            // Empty-state keys off the rows the <ul/> renders, so the two can
            // never disagree about whether there is a run to show.
            isEmpty={() => props.rows().length === 0}
            loading={<CardGridSkeleton listClass="ci__list" />}
            empty={<div class="list-empty">{t('ci.empty')}</div>}
          >
            {() => (
              <ul class="row-list">
                {/* A re-read keeps an unchanged row's object (TanStack's structural
                    sharing), so <For/>'s referential keying re-renders only the
                    runs that actually moved. */}
                <For each={props.rows()}>
                  {(run) => <CiRunItem run={run} onViewLog={props.onViewLog} />}
                </For>
              </ul>
            )}
          </QueryBoundary>
        </Panel>
      </Show>

      <Toast
        message={props.toast()?.message ?? null}
        tone={props.toast()?.tone ?? 'success'}
        onDismiss={props.onDismissToast}
      />
    </section>
  );
}
